import { useRef, useState } from 'react'
import { Button } from '@web/components/ui/Button'
import { useAudio } from '@web/lib/audio'
import type { ExerciseRendererProps } from './types'

/**
 * Hear it, then answer. The one exercise type where the audio *is* the
 * question rather than a helper, so a missing clip has to be handled as a
 * broken exercise rather than a missing nicety.
 */
export function ListeningExercise({
  content,
  response,
  onChange,
  locked,
}: ExerciseRendererProps<'listening'>) {
  const { resolve } = useAudio()
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [plays, setPlays] = useState(0)
  const [failed, setFailed] = useState(false)

  // Resolved through the lesson's phrase→clip map, the same path every other
  // Portuguese string uses.
  const clipId = resolve(content.transcript)
  const clipSrc = clipId ? `/media/${clipId}` : undefined
  const limitReached = content.playLimit !== undefined && plays >= content.playLimit

  function play() {
    const el = audioRef.current
    if (!el || limitReached) return
    el.currentTime = 0
    void el.play().catch(() => setFailed(true))
    setPlays((n) => n + 1)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center gap-2 rounded-xl bg-sand-100 px-4 py-6">
        <audio
          ref={audioRef}
          src={clipSrc}
          preload="auto"
          onError={() => setFailed(true)}
        />
        <Button
          type="button"
          onClick={play}
          disabled={limitReached || failed || !clipSrc}
          size="lg"
        >
          <span aria-hidden="true">▶</span> Play
        </Button>
        {content.playLimit !== undefined && !failed && (
          <p className="text-xs text-ink-500">
            {Math.max(0, content.playLimit - plays)} play(s) left
          </p>
        )}
        {(failed || !clipSrc) && (
          <p className="text-center text-sm text-ink-500">
            This clip could not be played. You can still answer from the
            options below.
          </p>
        )}
      </div>

      {content.mode === 'choose' ? (
        <fieldset className="space-y-2.5">
          <legend className="sr-only">Choose what you heard</legend>
          {(content.options ?? []).map((option) => {
            const isChosen = response?.optionId === option.id
            const isCorrect = option.id === content.correctId
            let tone = 'ring-sand-200 bg-white'
            if (locked && isCorrect) tone = 'ring-brand-500 bg-brand-50'
            else if (locked && isChosen) tone = 'ring-clay-300 bg-clay-50'
            else if (isChosen) tone = 'ring-brand-600 ring-2 bg-brand-50'

            return (
              <button
                key={option.id}
                type="button"
                disabled={locked}
                aria-pressed={isChosen}
                onClick={() => onChange({ type: 'listening', optionId: option.id })}
                className={`min-h-13 w-full rounded-xl px-4 py-3 text-left ring-1 ${tone}`}
                lang="pt-BR"
              >
                {option.text}
              </button>
            )
          })}
        </fieldset>
      ) : (
        <div>
          <label htmlFor="transcribe" className="mb-1.5 block text-sm font-medium text-ink-700">
            Write what you hear
          </label>
          <textarea
            id="transcribe"
            rows={2}
            value={response?.text ?? ''}
            onChange={(e) => onChange({ type: 'listening', text: e.target.value })}
            disabled={locked}
            lang="pt-BR"
            autoCorrect="off"
            spellCheck={false}
            className="w-full resize-none rounded-xl bg-white px-4 py-3 text-base ring-1 ring-sand-200 focus:ring-2 focus:ring-brand-600 disabled:bg-sand-50"
          />
        </div>
      )}
    </div>
  )
}
